import ChartJS from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import Watermark from "./plugins/watermark";
import colorLib from "@kurkle/color";

import { parseJsonOrDefault } from "../utils/parseJsonOrDefault";
import { seriesColor } from "../chart-uplot/presets/palette";

ChartJS.register(ChartDataLabels);

function transparentize(value, opacity) {
  if (!value) return `rgba(99, 102, 241, ${opacity === undefined ? 0.5 : 1 - opacity})`;
  var alpha = opacity === undefined ? 0.5 : 1 - opacity;
  return colorLib(value).alpha(alpha).rgbString();
}

function createDatasets(rawData, chartType = "bar", stacked = false) {
  const barExtras = stacked ? {} : { borderRadius: 5, borderSkipped: false };

  // Handle new datasets structure
  if (rawData && rawData.datasets && Array.isArray(rawData.datasets)) {
    return rawData.datasets.map((dataset, index) => {
      const color = dataset.color || seriesColor(index);
      const base = {
        label: dataset.label,
        data: dataset.data,
        backgroundColor: transparentize(color, 0.5),
        borderColor: color,
        borderWidth: 2,
        order: index,
      };
      if (chartType === "line") {
        const fill = stacked ? (index === 0 ? "origin" : "-1") : true;
        return { ...base, fill, tension: 0, pointRadius: 3, pointHoverRadius: 5 };
      }
      return { ...base, ...barExtras };
    });
  }

  // Handle legacy object format
  const entries = Array.isArray(rawData) ? rawData : Object.entries(rawData);
  return entries.map(([label, data], index) => {
    const base = {
      label,
      data: data.data,
      backgroundColor: transparentize(data.color, 0.5),
      borderColor: data.color,
      borderWidth: 2,
      order: index,
    };
    if (chartType === "line") {
      const fill = stacked ? (index === 0 ? "origin" : "-1") : true;
      return { ...base, fill, tension: 0, pointRadius: 3, pointHoverRadius: 5 };
    }
    return { ...base, ...barExtras };
  });
}

export const Chart = {
  updated() {
    if (!this.el.chart) return;

    const rawData = parseJsonOrDefault(this.el.dataset.data, []);
    const labels = parseJsonOrDefault(this.el.dataset.labels, []);
    const title = this.el.dataset.title;
    const labelConfig = parseJsonOrDefault(this.el.dataset.labelConfig, {});
    const chartType = this.el.dataset.chartType || "bar";
    const stacked = parseJsonOrDefault(this.el.dataset.stacked, false);

    const data = {
      labels: labels,
      datasets: createDatasets(rawData, chartType, stacked),
    };

    this.el.chart.data.datasets = this.el.chart.data.datasets.filter((dataset) =>
      data.datasets.some((d) => d.label === dataset.label),
    );

    data.datasets.forEach((newDataset) => {
      const existingDataset = this.el.chart.data.datasets.find((d) => d.label === newDataset.label);

      if (existingDataset) {
        Object.assign(existingDataset, newDataset);
      } else {
        this.el.chart.data.datasets.push(newDataset);
      }
    });

    this.el.chart.data.labels = labels;
    this.el.chart.options.plugins.title.text = title;

    // Update datalabels formatter with new labelConfig
    this.el.chart.options.plugins.datalabels.formatter = function (value, _context) {
      if (typeof value === "object" && typeof value.label === "string") {
        return value.label;
      }

      let y;
      if (typeof value === "number") {
        y = value;
      } else {
        y = value.y;
      }

      if (typeof y === "number") {
        const roundedValue = Math.round(y);

        // Use label configuration to determine format
        if (labelConfig.hideZeroValues && roundedValue === 0) {
          return ""; // Hide zero values if configured
        }

        if (labelConfig.unit) {
          return roundedValue + labelConfig.unit; // Add unit if specified
        }

        return roundedValue; // Just show the number
      }

      return "";
    };

    // Respect labelConfig display toggle for showing/hiding data labels
    const showDataLabels = !(labelConfig && labelConfig.display === false);
    this.el.chart.options.plugins.datalabels.display = showDataLabels;

    this.el.chart.update();
  },

  mounted() {
    const $chart = this.el.querySelector("canvas");
    const rawData = parseJsonOrDefault(this.el.dataset.data, []);
    const labels = parseJsonOrDefault(this.el.dataset.labels, []);
    const title = this.el.dataset.title;
    const legend = this.el.dataset.legend;
    const interactive = this.el.dataset.interactive === "true";
    const hasWatermark = this.el.dataset.watermark === "true";
    const watermarkSrc = this.el.dataset.watermarkSrc;
    const watermarkOpacity = this.el.dataset.watermarkOpacity;
    const labelConfig = parseJsonOrDefault(this.el.dataset.labelConfig, {});
    const stacked = parseJsonOrDefault(this.el.dataset.stacked, false);
    const chartType = this.el.dataset.chartType || "bar";
    const horizontal = parseJsonOrDefault(this.el.dataset.horizontal, false);
    const showTooltip = parseJsonOrDefault(this.el.dataset.tooltip, false);
    const clickNearest = parseJsonOrDefault(this.el.dataset.clickNearest, false);

    const data = {
      labels: labels,
      datasets: createDatasets(rawData, chartType, stacked),
    };

    let hoveredKey = null;

    const updateHoverColors = (chart, targetDsIdx, targetBarIdx) => {
      chart.data.datasets.forEach((dataset, dsIdx) => {
        const color = dataset.borderColor;
        if (targetBarIdx === null) {
          dataset.backgroundColor = transparentize(color, 0.5);
        } else {
          // targetDsIdx === null means group mode: highlight all datasets at the bar index
          dataset.backgroundColor = dataset.data.map((_, barIdx) =>
            (targetDsIdx === null || dsIdx === targetDsIdx) && barIdx === targetBarIdx
              ? transparentize(color, 0.2)
              : transparentize(color, 0.5),
          );
        }
      });
      chart.update("none");
    };

    const config = {
      type: chartType,
      data: data,
      plugins: [ChartDataLabels, hasWatermark ? Watermark : undefined].filter(Boolean),
      options: {
        indexAxis: horizontal ? "y" : "x",
        scales: {
          x: {
            stacked: stacked,
            beginAtZero: horizontal ? true : undefined,
            grace: horizontal ? "10%" : undefined,
            ticks: horizontal
              ? {
                  stepSize: 1,
                  callback: function (value) {
                    return Number.isInteger(value) ? value : "";
                  },
                }
              : {
                  font: {
                    size: 14,
                    weight: "bold",
                  },
                },
          },
          y: {
            stacked: stacked,
            beginAtZero: horizontal ? undefined : true,
            grace: horizontal ? undefined : "10%",
            ticks: horizontal
              ? {
                  font: {
                    size: 12,
                  },
                }
              : {
                  stepSize: 1,
                  callback: function (value) {
                    return Number.isInteger(value) ? value : "";
                  },
                },
          },
        },
        maintainAspectRatio: false,
        animation: {
          duration: 800,
          easing: "easeOutExpo",
        },
        transitions: {
          resize: 100,
        },
        onClick: (event, elements, chart) => {
          if (!interactive) return;
          let element;
          if (clickNearest) {
            const clickedElements = chart.getElementsAtEventForMode(
              event,
              "nearest",
              { intersect: true },
              false,
            );
            if (!clickedElements.length) return;
            element = clickedElements[0];
          } else {
            if (!elements.length) return;
            element = elements[0];
          }
          const clickedRaw = chart.data.datasets[element.datasetIndex].data[element.index];
          const pointMeta =
            typeof clickedRaw === "object" && clickedRaw !== null
              ? {
                  metric_slug: clickedRaw.metric_slug,
                  app_id: clickedRaw.app_id,
                  tv_id: clickedRaw.tv_id,
                }
              : {};
          const values = chart.data.datasets.map((ds) => {
            const raw = ds.data[element.index];
            const value = typeof raw === "object" && raw !== null ? raw.y : raw;
            return { label: ds.label, value };
          });
          this.pushEvent("chart-clicked", {
            id: this.el.id,
            label: chart.data.labels[element.index],
            index: element.index,
            dataset_index: element.datasetIndex,
            values,
            ...pointMeta,
          });
        },
        onHover: (event, elements, chart) => {
          if (!(interactive || showTooltip)) return;
          let newKey, targetDsIdx, targetBarIdx;
          if (clickNearest) {
            const nearest = chart.getElementsAtEventForMode(
              event,
              "nearest",
              { intersect: true },
              false,
            );
            newKey = nearest.length ? `${nearest[0].datasetIndex}-${nearest[0].index}` : null;
            targetDsIdx = nearest[0]?.datasetIndex ?? null;
            targetBarIdx = nearest[0]?.index ?? null;
          } else {
            newKey = elements.length ? `group-${elements[0].index}` : null;
            targetDsIdx = null;
            targetBarIdx = elements[0]?.index ?? null;
          }
          if (newKey === hoveredKey) return;
          hoveredKey = newKey;
          updateHoverColors(chart, targetDsIdx, targetBarIdx);
        },
        responsive: true,
        interaction: {
          mode: horizontal ? "y" : "index",
          intersect: false,
          events: [
            ...(interactive || showTooltip ? ["mousemove", "mouseout"] : []),
            ...(interactive ? ["click", "touchstart"] : []),
          ],
        },
        plugins: {
          chartWatermark: {
            src: watermarkSrc,
            opacity: watermarkOpacity && Number(watermarkOpacity),
          },
          legend: {
            display: true,
            position: legend,
          },
          tooltip: {
            enabled: showTooltip,
            mode: labelConfig.tooltipMode ?? "index",
            intersect: false,
            callbacks: {
              label: function (context) {
                const value = context.raw;
                let y;
                if (typeof value === "number") {
                  y = value;
                } else {
                  y = value?.y;
                }

                if (typeof y === "number") {
                  const roundedValue = Math.round(y);
                  const valueWithUnit = labelConfig.unit
                    ? `${roundedValue}${labelConfig.unit}`
                    : `${roundedValue}`;
                  return `${context.dataset.label}: ${valueWithUnit}`;
                }

                return context.formattedValue;
              },
            },
          },
          title: {
            text: title,
            display: !!title,
            font: {
              weight: "bold",
              size: 16,
            },
          },
          datalabels: {
            display: !(labelConfig && labelConfig.display === false),
            anchor: "end",
            align: horizontal ? "right" : "end",
            rotation: horizontal ? 0 : -90,
            offset: 3,
            clamp: true,
            font: {
              size: 10,
              weight: "bold",
            },
            // Show value based on label configuration
            formatter: function (value, _context) {
              if (typeof value === "object" && typeof value.label === "string") {
                return value.label;
              }

              let y;
              if (typeof value === "number") {
                y = value;
              } else {
                y = value.y;
              }

              if (typeof y === "number") {
                const roundedValue = Math.round(y);

                // Use label configuration to determine format
                if (labelConfig.hideZeroValues && roundedValue === 0) {
                  return ""; // Hide zero values if configured
                }

                if (labelConfig.unit) {
                  return roundedValue + labelConfig.unit; // Add unit if specified
                }

                return roundedValue; // Just show the number
              }

              return "";
            },
          },
        },
      },
    };

    this.el.chart = new ChartJS($chart, config);
  },

  destroyed() {},
};
